import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, Users, Truck, Boxes, Store, Files as FilesIcon,
  Settings as SettingsIcon, Menu, X, Search, LogOut, ChevronRight, Bell, MessageSquare, ClipboardCheck, CalendarDays, Receipt, Inbox, Globe, ExternalLink,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useClientSections from "@/hooks/useClientSections";
import { Thumb } from "@/components/Bits";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/projects", label: "Projects", icon: FolderOpen, testId: "nav-projects" },
  { to: "/inquiries", label: "Inquiries", icon: Inbox, testId: "nav-inquiries", staff: true },
  { to: "/content", label: "Content Studio", icon: Globe, testId: "nav-content", staff: true },
  { to: "/decisions", label: "Decisions", icon: ClipboardCheck, testId: "nav-decisions", clientOnly: true, badge: "decisions", section: "decisions" },
  { to: "/clients", label: "Clients", icon: Users, testId: "nav-clients", staff: true },
  { to: "/procurement", label: "Procurement", icon: Truck, testId: "nav-procurement", staff: true },
  { to: "/library", label: "Products / Library", icon: Boxes, testId: "nav-library", staff: true },
  { to: "/vendors", label: "Vendors", icon: Store, testId: "nav-vendors", staff: true },
  { to: "/messages", label: "Messages", icon: MessageSquare, testId: "nav-messages", badge: "messages" },
  { to: "/invoices", label: "Invoices & Payments", icon: Receipt, testId: "nav-invoices", section: "invoices" },
  { to: "/invoice-library", label: "Invoice Items", icon: Receipt, testId: "nav-invoice-library", staff: true },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar", staff: true },
  { to: "/calendar", label: "Timeline", icon: CalendarDays, testId: "nav-timeline", clientOnly: true, section: "timeline" },
  { to: "/files", label: "Files", icon: FilesIcon, testId: "nav-files", section: "documents" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

function GlobalSearch({ onNavigate }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) {
      setRes(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: q.trim() } });
        setRes(data);
      } catch {
        setRes(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => {
    setQ("");
    setRes(null);
    onNavigate?.();
    navigate(path);
  };

  const groups = res
    ? [
        ["Projects", res.projects.map((p) => ({ label: p.name, path: `/projects/${p.id}` }))],
        ["Clients", res.clients.map((c) => ({ label: c.name, path: `/clients/${c.id}` }))],
        ["Products", res.products.map((p) => ({ label: `${p.name}${p.sku ? ` · ${p.sku}` : ""}`, path: `/products/${p.id}` }))],
        ["Vendors", res.vendors.map((v) => ({ label: v.name, path: `/vendors/${v.id}` }))],
        ["Spaces", res.spaces.map((s) => ({ label: s.name, path: `/spaces/${s.id}` }))],
        ["Orders", res.procurement.map((i) => ({ label: i.po_number || i.vendor_order_number || i.status, path: `/procurement/${i.id}` }))],
      ].filter(([, items]) => items.length)
    : [];

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" strokeWidth={1.5} />
      <input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search projects, products, SKUs, POs…"
        className="w-full rounded-sm border border-line bg-white py-2 pl-9 pr-3 text-sm outline-none transition-colors placeholder:text-fog focus:border-lime"
      />
      {res && (
        <div data-testid="global-search-results" className="absolute left-0 right-0 top-11 z-50 max-h-80 overflow-auto card-surface p-2 shadow-lg">
          {groups.length === 0 && <div className="px-3 py-4 text-sm text-fog">No matches for “{q}”.</div>}
          {groups.map(([title, items]) => (
            <div key={title} className="mb-2">
              <div className="overline px-3 py-2">{title}</div>
              {items.map((it) => (
                <button
                  key={it.path + it.label}
                  onClick={() => go(it.path)}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-ink transition-colors hover:bg-bone"
                >
                  <span className="truncate">{it.label}</span>
                  <ChevronRight className="h-3.5 w-3.5 text-fog" strokeWidth={1.5} />
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [data, setData] = useState({ unread: 0, items: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const markAll = async () => {
    await api.post("/notifications/read", { ids: data.items.map((i) => i.id) }).catch(() => {});
    load();
  };

  return (
    <div className="relative px-1">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 rounded-sm border border-line bg-white px-3 py-2 text-sm text-stone transition-colors hover:border-fog"
      >
        <Bell className="h-4 w-4" strokeWidth={1.5} />
        Updates
        {data.unread > 0 && (
          <span data-testid="notification-count" className="num ml-auto rounded-full bg-lime px-2 py-[2px] text-[11px] text-ink">
            {data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notification-panel" className="absolute left-0 right-0 top-11 z-50 max-h-80 overflow-auto card-surface p-2 shadow-lg">
          {data.items.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                setOpen(false);
                if (n.entity_type === "presentation") navigate(`/presentations/${n.entity_id}`);
                else if (n.project_id) navigate(`/projects/${n.project_id}`);
              }}
              className={`block w-full px-3 py-2 text-left text-xs transition-colors hover:bg-bone ${n.read ? "text-stone" : "text-ink"}`}
            >
              <span className="block">{n.client_action || n.detail || n.action}</span>
              <span className="num mt-1 block text-[10px] text-fog">{n.project_name}</span>
            </button>
          ))}
          {!data.items.length && <div className="px-3 py-4 text-xs text-fog">No updates yet.</div>}
          {data.items.length > 0 && (
            <button data-testid="notification-mark-read" onClick={markAll} className="mt-2 w-full rounded-sm border border-line px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-stone hover:text-ink">
              Mark all read
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NavList({ onNavigate }) {
  const { isStaff } = useAuth();
  const sections = useClientSections();
  const [unread, setUnread] = useState(0);
  const [decisions, setDecisions] = useState(0);
  const location = useLocation();

  useEffect(() => {
    api.get("/messages/unread").then((r) => setUnread(r.data.unread)).catch(() => {});
    if (!isStaff) {
      api.get("/decisions/summary").then((r) => setDecisions(r.data.needs_review)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const counts = { messages: unread, decisions };

  return (
    <nav className="flex flex-col gap-[2px]">
      {NAV.filter((n) => (!n.staff || isStaff) && (!n.clientOnly || !isStaff)
                  && (isStaff || !n.section || sections[n.section] !== false))
        .map(({ to, label, icon: Icon, testId, badge }) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          data-testid={testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-sm px-3 py-[10px] text-sm transition-colors duration-200 ${
              isActive ? "bg-[#EDEFC4] text-ink font-medium" : "text-stone hover:bg-line/60 hover:text-ink"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span className="truncate">{label}</span>
          {badge && counts[badge] > 0 && (
            <span data-testid={`nav-${badge}-unread`} className="num ml-auto rounded-full bg-lime px-2 py-[1px] text-[10px] text-ink">
              {counts[badge]}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function WebsiteAdminLink() {
  return (
    <a
      href="https://asthetik.design/admin"
      data-testid="nav-website-admin"
      className="mt-4 flex items-center gap-3 rounded-sm border border-line px-3 py-[10px] text-sm text-stone transition-colors hover:border-fog hover:text-ink"
    >
      <ExternalLink className="h-4 w-4 shrink-0" strokeWidth={1.5} />
      <span className="truncate">Back to Website Admin</span>
    </a>
  );
}

function Wordmark() {
  return (
    <Link to="/" data-testid="brand-wordmark" className="block px-3 py-2">
      <div className="font-serif text-[22px] leading-none tracking-tight text-ink">
        Ästhetik<span className="text-lime">.</span>
      </div>
      <div className="overline mt-2">Design Co · OS</div>
    </Link>
  );
}

function UserBlock() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("");
  if (user?.preview)
    return (
      <div className="border-t border-line pt-4 px-3 text-[11px] leading-relaxed text-fog">
        You are previewing this portal as your client. Nothing here can be changed.
      </div>
    );
  return (
    <div className="border-t border-line pt-4">
      <button
        data-testid="account-settings-link"
        onClick={() => navigate("/settings")}
        className="flex w-full items-center gap-3 rounded-sm px-3 py-2 text-left transition-colors hover:bg-line/60"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-medium text-white">
          {initials}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-sm text-ink">{user?.name}</span>
          <span className="block truncate text-[11px] uppercase tracking-[0.15em] text-fog">{user?.role}</span>
        </span>
      </button>
      <button
        data-testid="logout-button"
        onClick={async () => {
          await logout();
          navigate("/login");
        }}
        className="mt-1 flex w-full items-center gap-3 rounded-sm px-3 py-2 text-sm text-stone transition-colors hover:bg-line/60 hover:text-ink"
      >
        <LogOut className="h-4 w-4" strokeWidth={1.5} />
        Log out
      </button>
    </div>
  );
}

function PreviewBanner({ user }) {
  return (
    <div data-testid="client-preview-banner"
      className="relative z-[60] flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-b border-ink bg-ink px-5 py-3 text-center text-xs text-white">
      <span className="uppercase tracking-[0.2em] text-lime">Client Preview Mode</span>
      <span className="text-white/80">
        Read-only view of {user?.preview_project_name || "this project"} — nothing you do here is
        saved, sent or charged.
      </span>
      <button
        data-testid="exit-client-preview-btn"
        onClick={() => { sessionStorage.removeItem("asthetik_preview_token"); window.close(); window.location.replace("/login"); }}
        className="rounded-sm border border-white/40 px-3 py-1 uppercase tracking-[0.15em] transition-colors hover:bg-white/10"
      >
        Exit preview
      </button>
    </div>
  );
}

export default function Shell({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { isStaff, user } = useAuth();
  const [projectCount, setProjectCount] = useState(0);
  const preview = !!user?.preview;

  useEffect(() => {
    if (!isStaff) api.get("/projects").then((r) => setProjectCount(r.data.length)).catch(() => {});
  }, [isStaff]);

  const showSearch = isStaff || projectCount > 1;

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-bone">
      {preview && <PreviewBanner user={user} />}
      {/* Desktop sidebar */}
      <aside className={`fixed left-0 top-0 hidden h-screen w-[264px] flex-col justify-between border-r border-line bg-white px-4 py-6 lg:flex ${preview ? "lg:top-11 lg:h-[calc(100vh-44px)]" : ""}`}>
        <div>
          <Wordmark />
          <div className="mt-8 mb-6 px-1">
            {isStaff ? <GlobalSearch /> : <NotificationBell />}
          </div>
          {!isStaff && showSearch && (
            <div className="mb-5 px-1">
              <GlobalSearch />
            </div>
          )}
          <NavList />
          {isStaff && <WebsiteAdminLink />}        </div>
        <UserBlock />
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-bone/85 px-5 py-4 backdrop-blur-xl lg:hidden">
        <Wordmark />
        <button
          data-testid="mobile-nav-toggle"
          onClick={() => setOpen(true)}
          className="rounded-sm border border-line p-2 text-ink transition-colors hover:bg-line/50"
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" strokeWidth={1.5} />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 flex h-full w-[300px] flex-col justify-between border-l border-line bg-white px-4 py-6 animate-rise">
            <div>
              <div className="flex items-center justify-between">
                <Wordmark />
                <button
                  data-testid="mobile-nav-close"
                  onClick={() => setOpen(false)}
                  className="rounded-sm p-2 text-stone hover:bg-line/50"
                  aria-label="Close navigation"
                >
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="mt-6 mb-6 px-1">
                {isStaff ? <GlobalSearch onNavigate={() => setOpen(false)} /> : <NotificationBell />}
              </div>
              <NavList onNavigate={() => setOpen(false)} />
              {isStaff && <WebsiteAdminLink />}
            </div>
            <UserBlock />
          </div>
        </div>
      )}

      <main className="lg:pl-[264px]">
        <div className="mx-auto max-w-[1500px] px-5 py-8 sm:px-8 sm:py-10 lg:px-12 lg:py-12">{children}</div>
      </main>
    </div>
  );
}

export { Thumb };
